/**
 * Die Rechnungskarte im Chat — was sie zeigt und wo sie hängt.
 * (05.10.2026, Ticket 2996)
 *
 * ## Woher das kommt
 *
 * Martin, 05.10. 08:51, nachdem Vespera ihm einen Entwurf angelegt hatte:
 * Nach dem Anlegen oder Ändern einer Rechnung will er im Chat einen Knopf
 * („Rechnung öffnen“), über den er die Rechnung direkt im Panel aufruft —
 * statt eines nackten Links im Fließtext
 * (https://panel.ssi.at/faktura/invoices/9017).
 *
 * Die Rechnungs-Werkzeuge antworten mit einem Text, nicht mit einem
 * Datensatz — das Modell liest ihn. Diese Datei liest dieselben Zeilen und
 * macht daraus, was die Karte braucht. Gebaut wird die Karte in
 * tool-cards.ts (dort liegen card(), cardTitle() und appendKV()).
 *
 * ⚠️ Die Muster lesen die ECHTE Ausgabe der Werkzeuge. Damit sie nicht
 *    davondriften, kommen die Texte in tests/frontend/rechnung-karte-2996.
 *    test.ts aus den Werkzeugen selbst (wie bei schritte.ts).
 * ⚠️ Eine Karte gibt es nur, wenn der Server die Zeile „Karte: …“ mitschickt
 *    (tools/faktura.ts, karteZeile — Schalter FAKTURA_KARTE=0). Ohne sie
 *    bleibt die bisherige Textkarte; ältere Züge im Verlauf sehen aus wie
 *    vorher.
 * ⚠️ Der Knopf führt NUR auf panel.ssi.at. Der Text kommt aus Daten des
 *    Panels (Kundenname, Bezeichnungen) — eine Zeile „Link: …“ mit einer
 *    anderen Adresse wird nie ein Knopf.
 */

import { ART_KLASSE, FALTE_KLASSE, haengeKarteAn } from "./schritte.js";

/** Die Werkzeuge, deren Antwort eine Rechnung als Ergebnis trägt. */
const RECHNUNGS_WERKZEUGE: ReadonlySet<string> = new Set([
  "faktura_invoice_create",
  "faktura_invoice_clone",
  "faktura_invoice_get",
  "faktura_invoice_update",
  "faktura_invoice_item_add",
]);

/**
 * Unter diesem Namen hängen alle Rechnungskarten eines Zuges — die Karte
 * gehört der Rechnung, nicht dem Werkzeug, das sie zuletzt berührt hat.
 */
export const RECHNUNG_FAMILIE = "faktura_rechnung";

/** Die Klasse der Karte (tool-cards.ts, card("rechnung")). */
const RECHNUNG_KLASSE = "tool-rechnung";

export interface RechnungsKarte {
  id: number;
  /** Die Rechnung im Panel — immer https://panel.ssi.at/faktura/invoices/<id>. */
  link: string;
  /** Das PDF über den eigenen Server (server/faktura-routes.ts). */
  pdf: string;
  nummer: string | null;
  kunde: string | null;
  firma: string | null;
  brutto: number | null;
  netto: number | null;
  /** Die Summe steht unter Vorbehalt (ein Rabatt fehlt noch). */
  summeOffen: boolean;
  status: string | null;
  /** Zeilen, die nicht in einer hübschen Karte verschwinden dürfen. */
  warnungen: string[];
}

const LINK = /^Link: (https:\/\/panel\.ssi\.at\/faktura\/invoices\/(\d+))\s*$/gm;
const KARTE = /^Karte: /m;
const BETRAG = "(?:€|EUR)\\s*(-?\\d+(?:\\.\\d+)?)";

function alsText(result: unknown): string {
  if (typeof result !== "string") return "";
  const t = result.trim();
  // Ein doppelt verpackter Text (JSON in Anführungszeichen) — einmal auspacken.
  if (t.startsWith('"')) {
    try {
      const innen: unknown = JSON.parse(t);
      if (typeof innen === "string") return innen;
    } catch {
      /* kein JSON-Text */
    }
  }
  return result;
}

function zeile(text: string, muster: RegExp): string | null {
  const wert = muster.exec(text)?.[1]?.trim() ?? "";
  return wert === "" ? null : wert;
}

/**
 * Wie {@link zeile}, aber die LETZTE Fundstelle — wie beim Link. Was das
 * Werkzeug selbst schreibt (Stand, Firma), steht nach dem, was aus Daten
 * kommt (Kundenname, Bezeichnungen). Das Werkzeug stellt Daten seit der
 * Korrekturrunde in EINE Zeile; hier zählt trotzdem die letzte, nicht die erste.
 */
function letzteZeile(text: string, muster: RegExp): string | null {
  const alle = [...text.matchAll(new RegExp(muster.source, muster.flags.includes("g") ? muster.flags : `${muster.flags}g`))];
  const wert = alle[alle.length - 1]?.[1]?.trim() ?? "";
  return wert === "" ? null : wert;
}

/** „Gutschrift“ für GS-Nummern, sonst „Rechnung“ — so nennt die Karte den Beleg. */
export function belegArt(nummer: string | null): "Rechnung" | "Gutschrift" {
  return /^GS-/i.test(nummer ?? "") ? "Gutschrift" : "Rechnung";
}

function betrag(text: string, wort: string): number | null {
  // Der Betrag steht HINTER dem Wort („Brutto: €1344.00“, „brutto €1344.00“) —
  // die Positionszeilen („= €691.20 brutto“) tragen ihn davor und zählen nicht.
  const alle = [...text.matchAll(new RegExp(`\\b${wort}:?\\s*${BETRAG}`, "gi"))];
  const n = Number(alle[alle.length - 1]?.[1]);
  return Number.isFinite(n) ? n : null;
}

function statusWort(roh: string | null): string | null {
  if (!roh) return null;
  if (/bezahlt|paid/i.test(roh)) return "bezahlt";
  if (/^entwurf|^draft/i.test(roh)) return "Entwurf";
  if (/^sent|versendet/i.test(roh)) return "versendet";
  return roh;
}

/** So schreibt das Werkzeug, dass es die Firma nicht vergleichen konnte (tools/faktura.ts, firmaZeile). */
const UNGEPRUEFT = / — nicht gegen die übrigen Rechnungen geprüft \(/;

/**
 * Die Firma, wie die Karte sie nennt. Das Werkzeug schreibt „Name ·
 * Nummernkreis RE-3 (Firma-ID 3) — warum“: der Grund und die Kennung des
 * Panels sind für Vespera. Und der Nummernkreis steht schon im Titel, in der
 * Rechnungsnummer (DESIGN-NORM B6: keine Zeile wiederholt, was darüber
 * steht) — er bleibt nur, wenn es keinen Namen gibt.
 */
function firmaAus(text: string): string | null {
  const roh = letzteZeile(text, /^Firma: (.+?)(?: \(Firma-ID \d+\))?(?: — .*)?$/m);
  if (!roh) return null;
  const name = roh.replace(/ · Nummernkreis \S+$/, "") || roh;
  // Dritte Runde 05.10.: der Name hängt am Nummernkreis. Konnte das Werkzeug ihn
  // nicht gegen die übrigen Rechnungen halten (Liste oder Vergleichs-Rechnung
  // nicht lesbar), steht das auch auf der Karte — sonst behauptete sie mehr als
  // die Antwort („Firma SSI“ an einem Entwurf, der halb umgestellt sein kann).
  return UNGEPRUEFT.test(letzteZeile(text, /^(Firma: .+)$/m) ?? "") ? `${name} (nicht geprüft)` : name;
}

/**
 * Eine Warnung des Werkzeugs, wie Martin sie liest. Hinter dem Befund steht
 * für Vespera, was zu tun ist — mit Werkzeugnamen und dem Link, den die
 * Karte ohnehin als Knopf trägt. Das fällt weg; der Befund bleibt ganz.
 */
function fuerMenschen(warnung: string): string {
  const i = warnung.search(/https?:\/\/|faktura_/);
  if (i < 0) return warnung;
  const vor = warnung.slice(0, i);
  const satz = vor.lastIndexOf(". ");
  return (satz > 20 ? vor.slice(0, satz + 1) : vor).replace(/[\s(:→—–-]+$/u, "");
}

/** Der Dateiname des PDFs in der Adresse — nur Zeichen, die die Route durchlässt. */
function pdfDatei(nummer: string | null, id: number): string {
  const kern = (nummer ?? String(id)).replace(/[^A-Za-z0-9._-]/g, "");
  return `Rechnung_${kern || id}.pdf`;
}

/**
 * Aus der Antwort eines Rechnungs-Werkzeugs lesen, was die Karte zeigt —
 * oder null: kein Rechnungs-Werkzeug, keine Karte bestellt, kein Panel-Link
 * (ein STOPP, ein Fehler, ein älterer Zug).
 */
export function leseRechnungsKarte(tool: string, result: unknown): RechnungsKarte | null {
  if (!RECHNUNGS_WERKZEUGE.has(tool)) return null;
  const text = alsText(result);
  if (!text || !KARTE.test(text)) return null;
  const links = [...text.matchAll(LINK)];
  const letzter = links[links.length - 1];
  const id = Number(letzter?.[2]);
  if (!letzter?.[1] || !Number.isInteger(id) || id <= 0) return null;
  const nummer =
    zeile(text, /^Rechnungsnummer: (\S+)\s*$/m) ??
    zeile(text, /^Rechnung (\S+) \(ID \d+\)\s*$/m) ??
    zeile(text, /^Rechnung (\S+) aktualisiert\.\s*$/m);
  return {
    id,
    link: letzter[1],
    pdf: `/api/faktura/invoices/${id}/pdf/${pdfDatei(nummer, id)}`,
    nummer,
    kunde: zeile(text, /^Kunde: (.+)$/m),
    firma: firmaAus(text),
    brutto: betrag(text, "brutto"),
    netto: betrag(text, "netto"),
    summeOffen: /nicht bestätigt/.test(text),
    status: statusWort(letzteZeile(text, /^Status: (.+)$/m)),
    warnungen: text
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => /^(ACHTUNG|NICHT übernommen)/.test(l))
      .map(fuerMenschen),
  };
}

/**
 * „1.344,00 €“ — der Betrag, wie er auf einer Rechnung steht.
 *
 * ⚠️ Von Hand gesetzt, nicht über toLocaleString: „de-AT“ trennt Tausender
 *    je nach Browser mit einem Punkt oder einem schmalen Leerzeichen — auf
 *    einer Rechnung soll derselbe Betrag überall gleich aussehen.
 */
export function euro(n: number): string {
  const [ganz = "0", cent = "00"] = Math.abs(n).toFixed(2).split(".");
  return `${n < 0 ? "-" : ""}${ganz.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${cent} €`;
}

export function istRechnungsKarte(el: Element | null | undefined): el is HTMLElement {
  return el instanceof HTMLElement && el.classList.contains(RECHNUNG_KLASSE);
}

/**
 * Die Rechnungskarte in den Behälter ihres Zuges hängen.
 *
 * Martin, 15.09.: „Ich habe für eine Rechnung hunderte Cards offen.“ Darum
 * gibt es je Rechnung und Zug EINE Karte: legt Vespera an, hängt zwei
 * Positionen an und liest nach, steht am Ende der letzte Stand — an der
 * Stelle, an der die Rechnung zuerst erschien. Verschiedene Rechnungen
 * desselben Zuges faltet haengeKarteAn wie jede andere Karte.
 */
export function haengeRechnungsKarteAn(behaelter: HTMLElement, karte: HTMLElement): void {
  const id = karte.dataset.rechnung ?? "";
  const alt = Array.from(behaelter.querySelectorAll<HTMLElement>(`.${RECHNUNG_KLASSE}`)).find(
    (k) => k !== karte && k.dataset.rechnung === id,
  );
  if (!alt) {
    haengeKarteAn(behaelter, karte, RECHNUNG_FAMILIE);
    return;
  }
  karte.dataset.werkzeug = RECHNUNG_FAMILIE;
  // In einer Faltung nennt der Kopf die Art — die neue Karte schweigt wie die alte.
  if (alt.parentElement?.classList.contains(FALTE_KLASSE)) {
    for (const art of karte.querySelectorAll<HTMLElement>(`.${ART_KLASSE}`)) art.hidden = true;
  }
  alt.replaceWith(karte);
}
